import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FloppyDisk, ArrowCounterClockwise, Plus, Trash, Layout, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const SITE_FIELDS = [
  { section: "Hero", fields: [
    { k: "hero_headline_1", label: "Headline — before highlight" },
    { k: "hero_headline_hl", label: "Headline — highlighted word" },
    { k: "hero_headline_2", label: "Headline — after highlight" },
    { k: "hero_subheadline", label: "Sub-headline", area: true },
    { k: "hero_cta", label: "Primary button text" },
    { k: "hero_badge", label: "Trust badge line" },
  ]},
  { section: "Section headings", fields: [
    { k: "who_title", label: "\"Who it's for\" title" },
    { k: "how_title", label: "\"How it works\" title" },
    { k: "paystructure_title", label: "Pay structure title" },
    { k: "why_title", label: "\"Why choose\" title" },
    { k: "why_subtitle", label: "\"Why choose\" subtitle", area: true },
    { k: "pricing_title_1", label: "Pricing title — before highlight" },
    { k: "pricing_title_hl", label: "Pricing title — highlighted" },
    { k: "testimonials_title", label: "Testimonials title" },
    { k: "faq_title", label: "FAQ title" },
  ]},
  { section: "Final call-to-action & footer", fields: [
    { k: "cta_title", label: "CTA heading" },
    { k: "cta_subtitle", label: "CTA subtitle" },
    { k: "cta_button", label: "CTA button text" },
    { k: "footer_tagline", label: "Footer tagline", area: true },
  ]},
];

const EMAIL_FIELDS = [
  { section: "Branding", fields: [
    { k: "brand_name", label: "Brand name (email headers)" },
    { k: "footer", label: "Email footer text", area: true },
  ]},
  { section: "Payday summary", fields: [
    { k: "payday_summary_subject", label: "Subject", hint: "tokens: {start} {end}" },
    { k: "payday_summary_intro", label: "Intro line", area: true, hint: "tokens: {name}" },
  ]},
  { section: "Underpayment alert", fields: [
    { k: "underpayment_subject", label: "Subject", hint: "tokens: {shortfall}" },
    { k: "underpayment_intro", label: "Intro line", area: true, hint: "tokens: {name}" },
  ]},
  { section: "Email import — confirmation", fields: [
    { k: "inbound_confirmation_subject", label: "Subject" },
    { k: "inbound_confirmation_intro", label: "Intro line", area: true, hint: "tokens: {name}" },
  ]},
  { section: "Email import — manual review", fields: [
    { k: "inbound_manual_subject", label: "Subject" },
    { k: "inbound_manual_intro", label: "Intro line", area: true, hint: "tokens: {name}" },
  ]},
  { section: "Prefixes", fields: [
    { k: "billing_subject_prefix", label: "Billing email title" },
    { k: "announcement_subject_prefix", label: "Announcement subject prefix" },
  ]},
];

const Field = ({ f, value, onChange }) => (
  <div>
    <Label className="text-xs">{f.label}{f.hint && <span className="text-muted-foreground ml-1.5">· {f.hint}</span>}</Label>
    {f.area ? (
      <Textarea data-testid={`content-${f.k}`} className="mt-1.5 bg-secondary/50" rows={2}
        value={value ?? ""} onChange={(e) => onChange(f.k, e.target.value)} />
    ) : (
      <Input data-testid={`content-${f.k}`} className="mt-1.5 bg-secondary/50"
        value={value ?? ""} onChange={(e) => onChange(f.k, e.target.value)} />
    )}
  </div>
);

export default function AdminContent() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("landing");
  const [site, setSite] = useState(null);
  const [email, setEmail] = useState(null);

  const { data } = useQuery({ queryKey: ["admin-content"], queryFn: async () => (await api.get("/admin/content")).data });
  useEffect(() => { if (data) { if (!site) setSite(data.site_content); if (!email) setEmail(data.email_templates); } }, [data]); // eslint-disable-line

  const saveSite = useMutation({
    mutationFn: async () => (await api.put("/admin/content", { site_content: site })).data,
    onSuccess: (d) => { setSite(d.site_content); qc.invalidateQueries({ queryKey: ["public-config"] }); toast.success("Landing page content saved — live now."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const saveEmail = useMutation({
    mutationFn: async () => (await api.put("/admin/content", { email_templates: email })).data,
    onSuccess: (d) => { setEmail(d.email_templates); toast.success("Email templates saved — live now."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const reset = useMutation({
    mutationFn: async (section) => (await api.post("/admin/content/reset", { [section]: true })).data,
    onSuccess: (d, section) => {
      if (section === "site_content") { setSite(d.site_content); qc.invalidateQueries({ queryKey: ["public-config"] }); }
      else setEmail(d.email_templates);
      toast.success("Reset to defaults.");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (!site || !email) return <div className="text-muted-foreground" data-testid="admin-content-loading">Loading…</div>;

  const setS = (k, v) => setSite({ ...site, [k]: v });
  const setE = (k, v) => setEmail({ ...email, [k]: v });
  const faqs = Array.isArray(site.faqs) ? site.faqs : [];
  const setFaq = (i, key, v) => { const next = faqs.map((f, j) => j === i ? { ...f, [key]: v } : f); setS("faqs", next); };
  const addFaq = () => setS("faqs", [...faqs, { q: "", a: "" }]);
  const delFaq = (i) => setS("faqs", faqs.filter((_, j) => j !== i));

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-content-page">
      <div className="inline-flex rounded-md border border-border p-0.5 bg-secondary/40">
        {[["landing", "Landing page", Layout], ["email", "Email templates", EnvelopeSimple]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`content-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold transition-colors duration-200 ${
              tab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-4 w-4" weight="bold" /> {label}
          </button>
        ))}
      </div>

      {tab === "landing" ? (
        <>
          {SITE_FIELDS.map((grp) => (
            <Card key={grp.section} className="p-5 bg-card border-border">
              <h2 className="font-bold mb-4">{grp.section}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {grp.fields.map((f) => <Field key={f.k} f={f} value={site[f.k]} onChange={setS} />)}
              </div>
            </Card>
          ))}

          {/* FAQs */}
          <Card className="p-5 bg-card border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold">FAQ items</h2>
              <Button type="button" variant="secondary" size="sm" onClick={addFaq} data-testid="content-faq-add" className="rounded-md">
                <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add question
              </Button>
            </div>
            <div className="space-y-4" data-testid="content-faq-list">
              {faqs.length === 0 && <p className="text-sm text-muted-foreground">No FAQs.</p>}
              {faqs.map((f, i) => (
                <div key={i} className="rounded-md bg-secondary/40 p-3 space-y-2" data-testid={`content-faq-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input data-testid={`content-faq-q-${i}`} className="bg-secondary/50" placeholder="Question"
                      value={f.q ?? ""} onChange={(e) => setFaq(i, "q", e.target.value)} />
                    <button onClick={() => delFaq(i)} data-testid={`content-faq-del-${i}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200 shrink-0">
                      <Trash className="h-4 w-4" weight="bold" />
                    </button>
                  </div>
                  <Textarea data-testid={`content-faq-a-${i}`} className="bg-secondary/50" rows={2} placeholder="Answer"
                    value={f.a ?? ""} onChange={(e) => setFaq(i, "a", e.target.value)} />
                </div>
              ))}
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button onClick={() => saveSite.mutate()} disabled={saveSite.isPending} data-testid="content-save-site" size="lg" className="rounded-md font-semibold">
              <FloppyDisk className="h-4 w-4 mr-2" weight="bold" /> {saveSite.isPending ? "Saving…" : "Save landing content"}
            </Button>
            <Button onClick={() => reset.mutate("site_content")} disabled={reset.isPending} data-testid="content-reset-site" variant="outline" className="rounded-md">
              <ArrowCounterClockwise className="h-4 w-4 mr-2" weight="bold" /> Reset to defaults
            </Button>
          </div>
        </>
      ) : (
        <>
          {EMAIL_FIELDS.map((grp) => (
            <Card key={grp.section} className="p-5 bg-card border-border">
              <h2 className="font-bold mb-4">{grp.section}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {grp.fields.map((f) => <Field key={f.k} f={f} value={email[f.k]} onChange={setE} />)}
              </div>
            </Card>
          ))}
          <p className="text-xs text-muted-foreground">Tokens like <code className="text-foreground">{"{name}"}</code>, <code className="text-foreground">{"{start}"}</code>, <code className="text-foreground">{"{end}"}</code>, <code className="text-foreground">{"{shortfall}"}</code> are replaced automatically when the email is sent.</p>
          <div className="flex items-center gap-3">
            <Button onClick={() => saveEmail.mutate()} disabled={saveEmail.isPending} data-testid="content-save-email" size="lg" className="rounded-md font-semibold">
              <FloppyDisk className="h-4 w-4 mr-2" weight="bold" /> {saveEmail.isPending ? "Saving…" : "Save email templates"}
            </Button>
            <Button onClick={() => reset.mutate("email_templates")} disabled={reset.isPending} data-testid="content-reset-email" variant="outline" className="rounded-md">
              <ArrowCounterClockwise className="h-4 w-4 mr-2" weight="bold" /> Reset to defaults
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
